import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import ClassificationBadge, { Classification, ClassificationMethod } from './ClassificationBadge';
import ClassificationOverrideModal from './ClassificationOverrideModal';

export interface DocumentItem {
  id: string;
  description: string;
  quantity: number | string | null;
  unit: string | null;
  unit_price: number | string | null;
  total_price: number | string | null;
  vat_rate: number | string | null;
  classification: Classification;
  classification_method: ClassificationMethod;
  classification_confidence: number | null;
  classification_reasoning: string | null;
  classified_client_ico: string | null;
  classified_client_name: string | null;
  account_code: string | null;
  is_payment?: boolean;
  output_invoice_number?: string | null;
  output_item_description?: string | null;
}

interface Props {
  docId: string;
  vendor?: string | null;
  currency?: string | null;   // mena dokladu (USD faktúra sa nesmie ukázať v €)
}

export const CURRENCY_SYMBOL: Record<string, string> = { EUR: '€', USD: '$', GBP: '£', CZK: 'Kč', PLN: 'zł', HUF: 'Ft', CHF: 'CHF' };
export const fmtMoney = (v: number | string | null | undefined, currency?: string | null): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (Number.isNaN(n)) return '—';
  const code = (currency || 'EUR').toUpperCase();
  return `${n.toFixed(2)} ${CURRENCY_SYMBOL[code] || code}`;
};

const DocumentItemsPanel: React.FC<Props> = ({ docId, vendor, currency }) => {
  const [items, setItems] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DocumentItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/classification/document/${docId}/items`);
      setItems(res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Chyba pri načítaní položiek');
    } finally {
      setLoading(false);
    }
  }, [docId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div style={{ padding: 12, color: '#6b7280', fontSize: 13 }}>Načítavam položky…</div>;
  }
  if (error) {
    return <div style={{ padding: 12, color: '#b91c1c', fontSize: 13 }}>{error}</div>;
  }
  if (items.length === 0) {
    return (
      <div style={{ padding: 12, color: '#6b7280', fontSize: 13, fontStyle: 'italic' }}>
        Doklad nemá žiadne extrahované položky.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
        {items.length} {items.length === 1 ? 'položka' : items.length < 5 ? 'položky' : 'položiek'} ·
        klikni na riadok pre manuálnu klasifikáciu
      </div>

      {/* Na mobile (mobile.css .doc-items-table) sa riadky zobrazia ako kartičky
          s popiskami z data-label — široká tabuľka by inak scrollovala do boku. */}
      <table className="doc-items-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <th style={th}>Popis</th>
            <th style={{ ...th, textAlign: 'right', whiteSpace: 'nowrap' }}>Suma</th>
            <th style={th}>Klasifikácia</th>
            <th style={th}>Klient / účet</th>
          </tr>
        </thead>
        <tbody>
          {items.map(it => (
            <tr
              key={it.id}
              onClick={() => setEditing(it)}
              style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <td style={td} data-label="Popis">
                <div style={{ fontWeight: 500 }}>{it.description}</div>
                {it.classification_reasoning && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }} title={it.classification_reasoning}>
                    {it.classification_reasoning.length > 110
                      ? it.classification_reasoning.slice(0, 110) + '…'
                      : it.classification_reasoning}
                  </div>
                )}
              </td>
              <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }} data-label="Suma">
                {fmtMoney(it.total_price, currency)}
              </td>
              <td style={td} data-label="Klasifikácia">
                {it.is_payment ? (
                  <span style={{
                    display: 'inline-block', padding: '4px 10px',
                    background: '#dbeafe', color: '#1e40af',
                    borderRadius: 4, fontSize: 12, fontWeight: 600,
                  }}>Platba / preddavok</span>
                ) : (
                  <ClassificationBadge
                    classification={it.classification}
                    method={it.classification_method}
                    confidence={it.classification_confidence}
                  />
                )}
              </td>
              <td style={td} data-label="Klient / účet">
                {it.is_payment ? (
                  <div style={{ fontSize: 11, color: '#6b7280' }}>vyúčtovanie 321/314</div>
                ) : (
                  <>
                    {it.classified_client_name && (
                      <div style={{ fontWeight: 500 }}>{it.classified_client_name}</div>
                    )}
                    {it.account_code && (
                      <div style={{ fontSize: 11, color: '#6b7280' }}>účet {it.account_code}</div>
                    )}
                    {it.output_invoice_number && (
                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                        výst. faktúra {it.output_invoice_number}
                      </div>
                    )}
                    {!it.classified_client_name && !it.account_code && !it.output_invoice_number && '—'}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <ClassificationOverrideModal
          item={editing}
          vendor={vendor}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontWeight: 600,
  fontSize: 12,
  color: '#374151',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};

const td: React.CSSProperties = {
  padding: '10px',
  verticalAlign: 'top',
};

export default DocumentItemsPanel;
